import React, { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import SyncIcon from "@mui/icons-material/Sync";
import BadgeIcon from "@mui/icons-material/Badge";
import { runWalletReconciliationHttp } from "../../../../services/kycService";
import {
  previewImportScannedDataset,
  runImportScannedDataset,
  previewSyncAllFirebaseClients,
  runSyncAllFirebaseClients,
  previewRefreshRegistrationPhotos,
  runRefreshRegistrationPhotos,
} from "../../../../services/kycBulkActions";

const ACTIONS = {
  import: {
    key: "import",
    label: "Import scan JSON",
    confirmText: "IMPORT SCAN JSON",
    buttonColor: { background: "linear-gradient(135deg, #0C2C50 0%, #450F5B 100%)" },
  },
  syncAll: {
    key: "syncAll",
    label: "Sync all Firebase clients",
    confirmText: "SYNC ALL FIREBASE CLIENTS",
    outlined: { borderColor: "rgba(42,245,152,0.5)", color: "#2af598" },
  },
  syncPhotos: {
    key: "syncPhotos",
    label: "Sync registration photos",
    confirmText: "SYNC REGISTRATION PHOTOS",
    outlined: { borderColor: "rgba(255,183,77,0.5)", color: "#ffb74d" },
  },
  reconcile: {
    key: "reconcile",
    label: "Run wallet reconciliation (all)",
    confirmText: "RUN WALLET RECONCILIATION",
    outlined: { borderColor: "rgba(126,184,255,0.5)", color: "#7eb8ff" },
  },
};

function formatPreview(actionKey, summary) {
  if (actionKey === "reconcile") {
    return [
      "Wallet reconciliation will run for every role-1 user.",
      "Wallet addresses, auto_pay_eligible_from and edit history are not changed by this panel.",
      "The server may create a KYC stub if a profile is missing — merge C2 will move this behind an admin gate.",
    ];
  }
  if (!summary) return ["Building preview…"];
  const skipped = Object.entries(summary.skipped || {})
    .filter(([, n]) => n)
    .map(([k, n]) => `${k.replace(/_/g, " ")}: ${n}`);
  const fields = Object.entries(summary.fieldCounts || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 12)
    .map(([name, n]) => `${name} × ${n}`);
  return [
    `Profiles to create: ${summary.createUids?.length || 0}`,
    `Orphan folders to create: ${summary.createOrphans || 0}`,
    `Existing profiles with empty fields to fill: ${summary.fillProfiles || 0}`,
    `Already complete (nothing to fill): ${summary.nothingToFill || 0}`,
    skipped.length ? `Skipped: ${skipped.join("; ")}` : "Skipped: 0",
    `Fields that would be filled: ${summary.fieldsTotal || 0}`,
    fields.length ? `Field names: ${fields.join(", ")}` : null,
    "Filled fields are never overwritten. Nothing is deleted.",
  ].filter(Boolean);
}

export default function KycImportPanel({ onDone }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [typed, setTyped] = useState("");
  const [importDataset, setImportDataset] = useState(null);

  const closeDialog = () => {
    if (busy) return;
    setDialog(null);
    setTyped("");
    setImportDataset(null);
  };

  const openPreview = async (actionKey, extra = {}) => {
    setError(null);
    setResult(null);
    setTyped("");
    setBusy(true);
    try {
      let preview = null;
      if (actionKey === "syncAll") preview = await previewSyncAllFirebaseClients();
      if (actionKey === "syncPhotos") preview = await previewRefreshRegistrationPhotos();
      if (actionKey === "import") preview = await previewImportScannedDataset(extra.dataset);
      setDialog({ actionKey, preview, ...extra });
    } catch (err) {
      setError(err.message || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const handlePickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const dataset = JSON.parse(await file.text());
      if (!Array.isArray(dataset?.clients)) {
        setError("JSON must have a clients array.");
        return;
      }
      setImportDataset(dataset);
      await openPreview("import", { dataset });
    } catch (_err) {
      setError("Could not read that JSON file.");
    }
  };

  const handleConfirm = async () => {
    if (!dialog) return;
    const action = ACTIONS[dialog.actionKey];
    if (typed !== action.confirmText) return;
    setBusy(true);
    setError(null);
    try {
      let res;
      if (dialog.actionKey === "import") res = await runImportScannedDataset(importDataset || dialog.dataset);
      if (dialog.actionKey === "syncAll") res = await runSyncAllFirebaseClients();
      if (dialog.actionKey === "syncPhotos") res = await runRefreshRegistrationPhotos();
      if (dialog.actionKey === "reconcile") {
        const data = await runWalletReconciliationHttp(null);
        res = { processed: data.processed };
      }
      setResult({ action: action.label, summary: res });
      setDialog(null);
      setTyped("");
      setImportDataset(null);
      onDone?.();
    } catch (err) {
      setError(err.message || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const confirmReady = dialog && typed === ACTIONS[dialog.actionKey].confirmText && !busy;

  return (
    <Accordion
      defaultExpanded={false}
      disableGutters
      sx={{
        mb: 2,
        bgcolor: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: "8px !important",
        "&:before": { display: "none" },
        color: "#fff",
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "rgba(255,255,255,0.6)" }} />}>
        <Box>
          <Typography variant="subtitle1" sx={{ color: "#2af598", fontWeight: 700 }}>
            Advanced actions
          </Typography>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.55)" }}>
            Closed by default. Import, Sync all and Sync photos only fill empty fields.
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Alert severity="info" sx={{ mb: 2 }}>
          Each action shows a count preview, then asks you to type the action name. Filled KYC
          fields are never overwritten.
        </Alert>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
          <Button
            variant="contained"
            component="label"
            startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <UploadFileIcon />}
            disabled={busy}
            sx={{ background: ACTIONS.import.buttonColor.background, fontWeight: 700 }}
          >
            Import scan JSON
            <input type="file" accept=".json,application/json" hidden onChange={handlePickFile} />
          </Button>
          <Button
            variant="outlined"
            startIcon={<SyncIcon />}
            onClick={() => openPreview("syncAll")}
            disabled={busy}
            sx={ACTIONS.syncAll.outlined}
          >
            Sync all Firebase clients
          </Button>
          <Button
            variant="outlined"
            startIcon={<BadgeIcon />}
            onClick={() => openPreview("syncPhotos")}
            disabled={busy}
            sx={ACTIONS.syncPhotos.outlined}
          >
            Sync registration photos
          </Button>
          <Button
            variant="outlined"
            onClick={() => setDialog({ actionKey: "reconcile", preview: null })}
            disabled={busy}
            sx={ACTIONS.reconcile.outlined}
          >
            Run wallet reconciliation (all)
          </Button>
        </Box>

        {result && (
          <Typography variant="body2" sx={{ color: "#aaa", mt: 2 }}>
            {result.action}:{" "}
            {result.summary?.processed != null
              ? `${result.summary.processed} processed`
              : `${result.summary?.createUids?.length || 0} created, ${result.summary?.fillProfiles || 0} filled, ${result.summary?.nothingToFill || 0} unchanged, ${result.summary?.errors || 0} errors`}
          </Typography>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Dialog
          open={!!dialog}
          onClose={closeDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { bgcolor: "#1a1a2e", color: "#fff" } }}
        >
          {dialog && (
            <>
              <DialogTitle>Confirm: {ACTIONS[dialog.actionKey].label}</DialogTitle>
              <DialogContent>
                {formatPreview(dialog.actionKey, dialog.preview).map((line) => (
                  <Typography key={line} variant="body2" sx={{ color: "rgba(255,255,255,0.8)", mb: 0.75 }}>
                    {line}
                  </Typography>
                ))}
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.55)", mt: 2, mb: 1 }}>
                  Type {ACTIONS[dialog.actionKey].confirmText} to continue.
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  autoComplete="off"
                  sx={{
                    "& .MuiInputBase-root": { color: "#fff" },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.2)" },
                  }}
                />
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={closeDialog} disabled={busy} sx={{ color: "rgba(255,255,255,0.7)" }}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  disabled={!confirmReady}
                  onClick={handleConfirm}
                  sx={{ bgcolor: "#2af598", color: "#0a1a12" }}
                >
                  {busy ? "Running…" : "Run"}
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </AccordionDetails>
    </Accordion>
  );
}
